/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function LaporanDinas() {
  const [loading, setLoading] = useState(false);
  const [dataLaporanDinas, setDataLaporanDinas] = useState(null);
  const [selectedTanggal, setSelectedTanggal] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const [userStatus, setUserStatus] = useState(null);

  // State untuk modal laporan
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPegawai, setSelectedPegawai] = useState(null);
  const [form, setForm] = useState({
    uid_RSS: "",
    tanggal: "",
    kegiatan: [
      {
        kegiatan: "",
        output: "",
        jumlah_output: "",
        satuan_output: "",
      },
    ],
  });

  // State untuk modal kegiatan
  const [openKegiatanModal, setOpenKegiatanModal] = useState(false);
  const [selectedKegiatan, setSelectedKegiatan] = useState([]);
  const [selectedLaporanTitle, setSelectedLaporanTitle] = useState("");
  const [loadingKegiatan, setLoadingKegiatan] = useState(false);
  const [openEditKegiatanModal, setOpenEditKegiatanModal] = useState(false);
  const [editingKegiatan, setEditingKegiatan] = useState(null);
  const [editKegiatanForm, setEditKegiatanForm] = useState({
    kegiatan: "",
    output: "",
    jumlah_output: "",
    satuan_output: "",
  });
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({
    type: "", // 'laporan_harian', 'instruksi', 'disposisi', 'events', 'tamu'
    title: "",
    data: [],
    tanggal: "",
    namaPegawai: "",
  });
  const [dataDaftarHadir, setDataDaftarHadir] = useState({}); // { uid_RSS: data }
  const [sortBy, setSortBy] = useState("bidang"); // bidang, aktivitas_desc, aktivitas_asc, level_jabatan
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const satuanOutputOptions = [
    "dokumen",
    "item",
    "kegiatan",
    "record",
    "program",
    "sistem",
    "laporan",
    "file",
    "data",
    "proses",
    "kunjungan",
    "rapat",
    "koordinasi",
    "verifikasi",
    "input",
    "update",
    "maintenance",
    "monitoring",
    "evaluasi",
    "pelatihan",
    "workshop",
    "seminar",
    "survey",
    "audit",
    "inspeksi",
    "pemeriksaan",
    "analisis",
    "penelitian",
    "pengembangan",
    "implementasi",
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid_RSS || user.uid);
    setUserDinasId(user.dinas_id);
    setUserStatus(user.status || user.role);

    // Set default tanggal ke hari ini dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);
    const todayWITA = witaDate.toISOString().split("T")[0];
    setSelectedTanggal(todayWITA);

    // Auto-select dinas berdasarkan user yang login
    if (user.dinas_id) {
      setUserDinasId(user.dinas_id);
    }
  }, []);

  const fetchDaftarHadir = async (uid_RSS) => {
    if (!uid_RSS || !selectedTanggal) {
      return null;
    }

    try {
      const response = await axios.post(
        "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/getdh/mingguan",
        {
          tAwal: selectedTanggal,
          tAkhir: selectedTanggal,
          idUser: uid_RSS,
        }
      );

      if (
        response.data &&
        response.data.success === 1 &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        return response.data.data[0]; // Ambil data pertama untuk tanggal tersebut
      }
      return null;
    } catch (err) {
      console.error("Error fetching daftar hadir:", err);
      return null;
    }
  };

  const fetchLaporanDinas = useCallback(async () => {
    if (!userDinasId || !selectedTanggal) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${userDinasId}/tanggal/${selectedTanggal}`
      );
      setDataLaporanDinas(res.data);

      // Fetch data daftar hadir untuk semua pegawai
      if (res.data && res.data.data_pegawai) {
        const daftarHadirMap = {};
        const fetchPromises = res.data.data_pegawai.map(async (pegawai) => {
          const daftarHadirData = await fetchDaftarHadir(pegawai.uid_RSS);
          if (daftarHadirData) {
            daftarHadirMap[pegawai.uid_RSS] = daftarHadirData;
          }
        });
        await Promise.all(fetchPromises);
        setDataDaftarHadir(daftarHadirMap);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching laporan dinas:", err);
      setLoading(false);
      swal("Error", "Gagal mengambil data laporan dinas", "error");
    }
  }, [userDinasId, selectedTanggal]);

  const handleTanggalChange = (e) => {
    setSelectedTanggal(e.target.value);
  };

  // Auto-fetch data ketika tanggal berubah
  useEffect(() => {
    if (userDinasId && selectedTanggal) {
      fetchLaporanDinas();
    }
  }, [selectedTanggal, userDinasId, fetchLaporanDinas]);

  // Fungsi untuk modal laporan
  const handleOpenDialog = (pegawai) => {
    setSelectedPegawai(pegawai);
    setForm({
      uid_RSS: pegawai.uid_RSS.toString(),
      tanggal: selectedTanggal,
      kegiatan: [
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPegawai(null);
    setForm({
      uid_RSS: "",
      tanggal: "",
      kegiatan: [
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
  };

  const handleAddKegiatan = () => {
    setForm({
      ...form,
      kegiatan: [
        ...form.kegiatan,
        {
          kegiatan: "",
          output: "",
          jumlah_output: "",
          satuan_output: "",
        },
      ],
    });
  };

  const handleRemoveKegiatan = (index) => {
    if (form.kegiatan.length > 1) {
      const newKegiatan = form.kegiatan.filter((_, i) => i !== index);
      setForm({
        ...form,
        kegiatan: newKegiatan,
      });
    }
  };

  const handleKegiatanChange = (index, field, value) => {
    const newKegiatan = [...form.kegiatan];
    newKegiatan[index][field] = value;
    setForm({
      ...form,
      kegiatan: newKegiatan,
    });
  };

  const handleSubmit = () => {
    // Validasi form
    if (!form.tanggal) {
      swal("Error", "Tanggal harus diisi", "error");
      return;
    }

    const hasEmptyKegiatan = form.kegiatan.some(
      (kegiatan) =>
        !kegiatan.kegiatan || !kegiatan.output || !kegiatan.satuan_output
    );

    if (hasEmptyKegiatan) {
      swal("Error", "Semua field kegiatan harus diisi", "error");
      return;
    }

    const hasInvalidJumlah = form.kegiatan.some(
      (kegiatan) => !kegiatan.jumlah_output || kegiatan.jumlah_output <= 0
    );

    if (hasInvalidJumlah) {
      swal("Error", "Jumlah output harus lebih dari 0", "error");
      return;
    }

    const submitData = {
      uid_RSS: form.uid_RSS.toString(),
      tanggal: form.tanggal,
      kegiatan: form.kegiatan.map((kegiatan) => ({
        ...kegiatan,
        jumlah_output: parseInt(kegiatan.jumlah_output) || 0,
      })),
    };

    // Create new laporan
    axios
      .post(`${API_ESA_OFFICE}laporan-harian`, submitData)
      .then((res) => {
        swal("Success", "Laporan harian berhasil dibuat", "success");
        handleCloseDialog();
        fetchLaporanDinas(); // Refresh data
      })
      .catch((err) => {
        console.error("Error creating laporan harian:", err);
        swal("Error", "Gagal membuat laporan harian", "error");
      });
  };

  // Fungsi untuk modal kegiatan
  const handleOpenKegiatanModal = (pegawai) => {
    // Cek apakah pegawai sudah lapor
    if (pegawai.status_laporan === "Belum Lapor") {
      swal(
        "Info",
        "Pegawai belum melaporkan kegiatan untuk tanggal ini",
        "info"
      );
      return;
    }

    // Cek apakah id_laporan tersedia
    if (!pegawai.id_laporan) {
      swal("Error", "Data laporan tidak ditemukan", "error");
      return;
    }

    setOpenKegiatanModal(true);
    setLoadingKegiatan(true);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle(
      `Kegiatan ${pegawai.nama_pegawai} - ${new Date(
        selectedTanggal
      ).toLocaleDateString("id-ID")}`
    );

    // Fetch kegiatan dari backend
    axios
      .get(`${API_ESA_OFFICE}laporan-harian/listkegiatan/${pegawai.id_laporan}`)
      .then((res) => {
        // Ambil data kegiatan dari response
        const kegiatanData = res.data?.kegiatan || [];
        setSelectedKegiatan(kegiatanData);
        setLoadingKegiatan(false);
      })
      .catch((err) => {
        console.error("Error fetching kegiatan:", err);
        swal("Error", "Gagal mengambil data kegiatan", "error");
        setSelectedKegiatan([]);
        setLoadingKegiatan(false);
      });
  };

  const handleCloseKegiatanModal = () => {
    setOpenKegiatanModal(false);
    setSelectedKegiatan([]);
    setSelectedLaporanTitle("");
    setLoadingKegiatan(false);
  };

  const handleEditKegiatan = (kegiatan) => {
    setEditingKegiatan(kegiatan);
    setEditKegiatanForm({
      kegiatan: kegiatan.kegiatan,
      output: kegiatan.output,
      jumlah_output: kegiatan.jumlah_output.toString(),
      satuan_output: kegiatan.satuan_output,
    });
    setOpenEditKegiatanModal(true);
  };

  const handleCloseEditKegiatanModal = () => {
    setOpenEditKegiatanModal(false);
    setEditingKegiatan(null);
    setEditKegiatanForm({
      kegiatan: "",
      output: "",
      jumlah_output: "",
      satuan_output: "",
    });
  };

  const handleUpdateKegiatan = () => {
    // Validasi form
    if (
      !editKegiatanForm.kegiatan ||
      !editKegiatanForm.output ||
      !editKegiatanForm.satuan_output
    ) {
      swal("Error", "Semua field harus diisi", "error");
      return;
    }

    if (
      !editKegiatanForm.jumlah_output ||
      editKegiatanForm.jumlah_output <= 0
    ) {
      swal("Error", "Jumlah output harus lebih dari 0", "error");
      return;
    }

    const updateData = {
      kegiatan: editKegiatanForm.kegiatan,
      output: editKegiatanForm.output,
      jumlah_output: parseInt(editKegiatanForm.jumlah_output),
      satuan_output: editKegiatanForm.satuan_output,
    };

    axios
      .put(
        `${API_ESA_OFFICE}laporan-harian/kegiatan/${editingKegiatan.id_kegiatan}`,
        updateData
      )
      .then((res) => {
        swal("Success", "Kegiatan berhasil diperbarui", "success");
        handleCloseEditKegiatanModal();
        // Refresh data kegiatan
        if (selectedKegiatan.length > 0) {
          const updatedKegiatan = selectedKegiatan.map((kegiatan) =>
            kegiatan.id_kegiatan === editingKegiatan.id_kegiatan
              ? { ...kegiatan, ...updateData }
              : kegiatan
          );
          setSelectedKegiatan(updatedKegiatan);
        }
      })
      .catch((err) => {
        console.error("Error updating kegiatan:", err);
        swal("Error", "Gagal memperbarui kegiatan", "error");
      });
  };

  const handleDeleteKegiatan = (kegiatan) => {
    swal({
      title: "Apakah Anda yakin?",
      text: "Kegiatan ini akan dihapus secara permanen!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axios
          .delete(
            `${API_ESA_OFFICE}laporan-harian/kegiatan/${kegiatan.id_kegiatan}`
          )
          .then((res) => {
            swal("Success", "Kegiatan berhasil dihapus", "success");
            // Remove kegiatan from list
            const updatedKegiatan = selectedKegiatan.filter(
              (item) => item.id_kegiatan !== kegiatan.id_kegiatan
            );
            setSelectedKegiatan(updatedKegiatan);
            fetchLaporanDinas();
          })
          .catch((err) => {
            console.error("Error deleting kegiatan:", err);
            swal("Error", "Gagal menghapus kegiatan", "error");
          });
      }
    });
  };

  const handleOpenDetailModal = (type, data, tanggal, namaPegawai) => {
    let title = "";
    switch (type) {
      case "laporan_harian":
        title = `Detail Laporan Harian - ${namaPegawai} - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "instruksi":
        title = `Detail Instruksi Selesai - ${namaPegawai} - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "disposisi":
        title = `Detail Disposisi Selesai - ${namaPegawai} - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "events":
        title = `Detail Event - ${namaPegawai} - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      case "tamu":
        title = `Detail Tamu - ${namaPegawai} - ${new Date(
          tanggal
        ).toLocaleDateString("id-ID")}`;
        break;
      default:
        title = "Detail";
    }
    setDetailModalData({
      type: type,
      title: title,
      data: data || [],
      tanggal: tanggal,
      namaPegawai: namaPegawai,
    });
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailModalData({
      type: "",
      title: "",
      data: [],
      tanggal: "",
      namaPegawai: "",
    });
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!dataLaporanDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Jabatan",
      "Bidang",
      "Pangkat",
      "Status Laporan",
      "Jumlah Kegiatan",
    ];

    let tableRows = "";
    dataLaporanDinas.data_pegawai.forEach((pegawai, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${pegawai.nama_pegawai || ""}</td>
          <td>${pegawai.nama_jabatan || ""}</td>
          <td>${pegawai.nama_bidang || "-"}</td>
          <td>${pegawai.pangkat || ""}</td>
          <td>${pegawai.status_laporan || ""}</td>
          <td>${pegawai.laporan?.jumlah_kegiatan || 0}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Laporan Harian Dinas - ${selectedTanggal}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Laporan Harian Dinas - ${
            dataLaporanDinas.nama_dinas
          } - ${new Date(selectedTanggal).toLocaleDateString("id-ID")}</h2>
          <h3>Statistik:</h3>
          <ul>
            <li>Total Pegawai: ${dataLaporanDinas.statistik.total_pegawai}</li>
            <li>Sudah Lapor: ${dataLaporanDinas.statistik.sudah_lapor}</li>
            <li>Belum Lapor: ${dataLaporanDinas.statistik.belum_lapor}</li>
            <li>Total Kegiatan: ${
              dataLaporanDinas.statistik.total_kegiatan
            }</li>
            <li>Persentase Kepatuhan: ${
              dataLaporanDinas.statistik.persentase_lapor
            }%</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `laporan-harian-dinas-${selectedTanggal}.xls`);
  };

  // Kolom untuk tabel data pegawai
  const columns = [
    { Header: "No", accessor: "index", width: "3%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "6%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "10%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "8%" },
    {
      Header: "Waktu Masuk & Apel Pagi",
      accessor: "masuk_apel_pagi",
      width: "10%",
    },
    {
      Header: "Laporan Harian & Aksi",
      accessor: "laporan_harian",
      width: "12%",
    },
    { Header: "Instruksi", accessor: "instruksi", width: "8%" },
    { Header: "Disposisi", accessor: "disposisi", width: "8%" },
    { Header: "Event", accessor: "events", width: "8%" },
    { Header: "Tamu", accessor: "tamu", width: "8%" },
    {
      Header: "Waktu Pulang & Apel Siang",
      accessor: "pulang_apel_siang",
      width: "10%",
    },
    {
      Header: "Status",
      accessor: "total_activities",
      width: "8%",
      Cell: ({ row }) => {
        const pegawai = row.original;
        // Hitung total aktivitas: Disposisi + Instruksi + Tamu + Event + Laporan
        // Gunakan data yang sudah divalidasi dari rows atau akses langsung
        const disposisiArray = Array.isArray(pegawai.disposisi_data)
          ? pegawai.disposisi_data
          : Array.isArray(pegawai.disposisi)
          ? pegawai.disposisi
          : [];
        const instruksiArray = Array.isArray(pegawai.instruksi_data)
          ? pegawai.instruksi_data
          : Array.isArray(pegawai.instruksi)
          ? pegawai.instruksi
          : [];
        const tamuArray = Array.isArray(pegawai.tamu_data)
          ? pegawai.tamu_data
          : Array.isArray(pegawai.tamu)
          ? pegawai.tamu
          : [];
        const eventsArray = Array.isArray(pegawai.events_data)
          ? pegawai.events_data
          : Array.isArray(pegawai.events)
          ? pegawai.events
          : [];
        const laporanArray = Array.isArray(pegawai.laporan_harian_data)
          ? pegawai.laporan_harian_data
          : Array.isArray(pegawai.laporan_harian)
          ? pegawai.laporan_harian
          : [];

        const totalDisposisi = disposisiArray.length;
        const totalInstruksi = instruksiArray.length;
        const totalTamu = tamuArray.length;
        const totalEvents = eventsArray.length;
        const totalLaporan = laporanArray.length;

        const totalActivities =
          totalDisposisi +
          totalInstruksi +
          totalTamu +
          totalEvents +
          totalLaporan;

        if (totalActivities === 0) {
          return (
            <Chip
              label="No Activity"
              color="error"
              size="small"
              variant="filled"
            />
          );
        }

        return (
          <Chip
            label={`${totalActivities} Aktivitas`}
            color="info"
            size="small"
            variant="filled"
          />
        );
      },
    },
    // Kolom aksi hanya untuk admin
    ...(userStatus === "admin"
      ? [
          {
            Header: "Aksi",
            accessor: "actions",
            width: "6%",
            Cell: ({ row }) => (
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleOpenDialog(row.original)}
              >
                Lapor
              </MDButton>
            ),
          },
        ]
      : []),
  ];

  // Fungsi untuk menghitung total aktivitas pegawai
  const calculateTotalActivities = (pegawai) => {
    const disposisiArray = Array.isArray(pegawai.disposisi)
      ? pegawai.disposisi
      : [];
    const instruksiArray = Array.isArray(pegawai.instruksi)
      ? pegawai.instruksi
      : [];
    const tamuArray = Array.isArray(pegawai.tamu) ? pegawai.tamu : [];
    const eventsArray = Array.isArray(pegawai.events) ? pegawai.events : [];
    const laporanArray = Array.isArray(pegawai.laporan_harian)
      ? pegawai.laporan_harian
      : [];

    return (
      disposisiArray.length +
      instruksiArray.length +
      tamuArray.length +
      eventsArray.length +
      laporanArray.length
    );
  };

  // Fungsi untuk sorting data pegawai
  const getSortedDataPegawai = () => {
    if (!dataLaporanDinas?.data_pegawai) return [];

    const dataPegawai = [...dataLaporanDinas.data_pegawai];

    switch (sortBy) {
      case "bidang":
        return dataPegawai.sort((a, b) => {
          // Pertama sorting berdasarkan bidang_id
          const bidangIdA = a.bidang_id || 0;
          const bidangIdB = b.bidang_id || 0;
          if (bidangIdA !== bidangIdB) {
            return bidangIdA - bidangIdB;
          }

          // Jika bidang_id sama, sorting berdasarkan level_jabatan
          const levelA = a.level_jabatan != null ? Number(a.level_jabatan) : 0;
          const levelB = b.level_jabatan != null ? Number(b.level_jabatan) : 0;
          if (levelA !== levelB) {
            return levelA - levelB;
          }

          // Jika level_jabatan juga sama, sorting berdasarkan nama_bidang (untuk fallback)
          const bidangA = (a.nama_bidang || "").toLowerCase();
          const bidangB = (b.nama_bidang || "").toLowerCase();
          return bidangA.localeCompare(bidangB);
        });

      case "aktivitas_desc":
        return dataPegawai.sort((a, b) => {
          const activitiesA = calculateTotalActivities(a);
          const activitiesB = calculateTotalActivities(b);
          return activitiesB - activitiesA; // Banyak ke sedikit
        });

      case "aktivitas_asc":
        return dataPegawai.sort((a, b) => {
          const activitiesA = calculateTotalActivities(a);
          const activitiesB = calculateTotalActivities(b);
          return activitiesA - activitiesB; // Sedikit ke banyak
        });

      case "level_jabatan":
        return dataPegawai.sort((a, b) => {
          // level_jabatan bisa berupa number atau string, konversi ke string
          // Jika level_jabatan null/undefined, gunakan nama_jabatan atau default ke 0
          const levelA =
            a.level_jabatan != null
              ? String(a.level_jabatan)
              : a.nama_jabatan
              ? String(a.nama_jabatan)
              : "0";
          const levelB =
            b.level_jabatan != null
              ? String(b.level_jabatan)
              : b.nama_jabatan
              ? String(b.nama_jabatan)
              : "0";

          // Jika keduanya adalah angka, bandingkan sebagai angka
          const numA = parseFloat(levelA);
          const numB = parseFloat(levelB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }

          // Jika bukan angka, bandingkan sebagai string
          return levelA.localeCompare(levelB);
        });

      default:
        return dataPegawai;
    }
  };

  // Rows untuk tabel data pegawai
  const sortedDataPegawai = getSortedDataPegawai();
  const rows =
    sortedDataPegawai.map((pegawai, index) => {
      const firstLaporan =
        pegawai.laporan_harian && pegawai.laporan_harian.length > 0
          ? pegawai.laporan_harian[0]
          : null;

      // Hitung total aktivitas: Disposisi + Instruksi + Tamu + Event + Laporan
      // Pastikan data adalah array, jika undefined/null gunakan array kosong
      const disposisiArray = Array.isArray(pegawai.disposisi)
        ? pegawai.disposisi
        : [];
      const instruksiArray = Array.isArray(pegawai.instruksi)
        ? pegawai.instruksi
        : [];
      const tamuArray = Array.isArray(pegawai.tamu) ? pegawai.tamu : [];
      const eventsArray = Array.isArray(pegawai.events) ? pegawai.events : [];
      const laporanArray = Array.isArray(pegawai.laporan_harian)
        ? pegawai.laporan_harian
        : [];

      const totalDisposisi = disposisiArray.length;
      const totalInstruksi = instruksiArray.length;
      const totalTamu = tamuArray.length;
      const totalEvents = eventsArray.length;
      const totalLaporan = laporanArray.length;

      const totalActivities =
        totalDisposisi +
        totalInstruksi +
        totalTamu +
        totalEvents +
        totalLaporan;

      return {
        index: index + 1,
        uid_RSS: pegawai.uid_RSS,
        nama_pegawai: pegawai.nama_pegawai,
        nama_jabatan: pegawai.nama_jabatan,
        nama_bidang: pegawai.nama_bidang || "-",
        pangkat: pegawai.pangkat,
        status_laporan: pegawai.status_laporan,
        id_laporan: pegawai.laporan?.id_laporan || null,
        jumlah_kegiatan: pegawai.laporan?.jumlah_kegiatan || 0,
        total_activities: totalActivities,
        // Simpan data array dengan nama berbeda untuk akses di Cell
        laporan_harian_data: laporanArray,
        instruksi_data: instruksiArray,
        disposisi_data: disposisiArray,
        events_data: eventsArray,
        tamu_data: tamuArray,
        laporan_harian: (
          <MDBox>
            {laporanArray.length > 0 ? (
              <MDBox>
                <MDBox mb={1}>
                  <Chip
                    label={`${laporanArray.length} Laporan`}
                    color="info"
                    size="small"
                    sx={{ mb: 0.5, cursor: "pointer" }}
                    onClick={() =>
                      handleOpenDetailModal(
                        "laporan_harian",
                        laporanArray,
                        selectedTanggal,
                        pegawai.nama_pegawai
                      )
                    }
                  />
                  <MDTypography variant="caption" color="text" display="block">
                    {laporanArray.reduce(
                      (sum, l) => sum + (l.jumlah_kegiatan || 0),
                      0
                    )}{" "}
                    Kegiatan
                  </MDTypography>
                </MDBox>
                {firstLaporan && (
                  <MDBox display="flex" gap={0.5} flexWrap="wrap">
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() =>
                        handleOpenKegiatanModal({
                          id_laporan: firstLaporan.id_laporan,
                          status_laporan: pegawai.status_laporan,
                          nama_pegawai: pegawai.nama_pegawai,
                        })
                      }
                      sx={{ fontSize: "0.7rem", px: 0.5, py: 0.25 }}
                    >
                      Lihat
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            )}
          </MDBox>
        ),
        masuk_apel_pagi: (
          <MDBox>
            {(() => {
              const daftarHadirData = dataDaftarHadir[pegawai.uid_RSS];
              return (
                <>
                  {daftarHadirData?.waktuMasuk && (
                    <MDBox mb={0.5}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {daftarHadirData.status === "Hadir" ||
                        daftarHadirData.status === "Dinas Luar"
                          ? "Masuk"
                          : "Lapor"}
                        : {daftarHadirData.waktuMasuk}
                      </MDTypography>
                      {daftarHadirData.status && (
                        <Chip
                          label={daftarHadirData.status}
                          color={
                            daftarHadirData.status === "Hadir"
                              ? "success"
                              : daftarHadirData.status === "Izin"
                              ? "warning"
                              : "error"
                          }
                          size="small"
                          variant="filled"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </MDBox>
                  )}
                  {pegawai.apel_pagi ? (
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      mt={0.5}
                    >
                      Apel: {pegawai.apel_pagi.jam_absen || "-"}
                    </MDTypography>
                  ) : (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={0.5}
                    >
                      Belum Apel Pagi
                    </MDTypography>
                  )}
                </>
              );
            })()}
          </MDBox>
        ),
        instruksi: (
          <MDBox>
            {(() => {
              const instruksiTerima =
                instruksiArray.filter((i) => i.jenis === "terima") || [];
              const instruksiBeri =
                instruksiArray.filter((i) => i.jenis === "beri") || [];
              const totalInstruksi = instruksiArray.length;

              if (totalInstruksi === 0) {
                return (
                  <MDTypography variant="caption" color="text.secondary">
                    -
                  </MDTypography>
                );
              }

              return (
                <MDBox>
                  {instruksiTerima.length > 0 && (
                    <MDBox mb={0.5}>
                      <Chip
                        label={`Terima: ${instruksiTerima.length}`}
                        color="success"
                        size="small"
                        sx={{ mb: 0.5, cursor: "pointer" }}
                        onClick={() =>
                          handleOpenDetailModal(
                            "instruksi",
                            instruksiTerima,
                            selectedTanggal,
                            pegawai.nama_pegawai
                          )
                        }
                      />
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {instruksiTerima[0]?.perihal || "-"}
                      </MDTypography>
                    </MDBox>
                  )}
                  {instruksiBeri.length > 0 && (
                    <MDBox>
                      <Chip
                        label={`Beri: ${instruksiBeri.length}`}
                        color="info"
                        size="small"
                        sx={{ mb: 0.5, cursor: "pointer" }}
                        onClick={() =>
                          handleOpenDetailModal(
                            "instruksi",
                            instruksiBeri,
                            selectedTanggal,
                            pegawai.nama_pegawai
                          )
                        }
                      />
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {instruksiBeri[0]?.perihal || "-"}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              );
            })()}
          </MDBox>
        ),
        disposisi: (
          <MDBox>
            {(() => {
              const disposisiTerima =
                disposisiArray.filter((d) => d.jenis === "terima") || [];
              const disposisiBeri =
                disposisiArray.filter((d) => d.jenis === "beri") || [];
              const totalDisposisi = disposisiArray.length;

              if (totalDisposisi === 0) {
                return (
                  <MDTypography variant="caption" color="text.secondary">
                    -
                  </MDTypography>
                );
              }

              return (
                <MDBox>
                  {disposisiTerima.length > 0 && (
                    <MDBox mb={0.5}>
                      <Chip
                        label={`Terima: ${disposisiTerima.length}`}
                        color="warning"
                        size="small"
                        sx={{ mb: 0.5, cursor: "pointer" }}
                        onClick={() =>
                          handleOpenDetailModal(
                            "disposisi",
                            disposisiTerima,
                            selectedTanggal,
                            pegawai.nama_pegawai
                          )
                        }
                      />
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {disposisiTerima[0]?.perihal ||
                          disposisiTerima[0]?.nomor_surat ||
                          "-"}
                      </MDTypography>
                    </MDBox>
                  )}
                  {disposisiBeri.length > 0 && (
                    <MDBox>
                      <Chip
                        label={`Beri: ${disposisiBeri.length}`}
                        color="info"
                        size="small"
                        sx={{ mb: 0.5, cursor: "pointer" }}
                        onClick={() =>
                          handleOpenDetailModal(
                            "disposisi",
                            disposisiBeri,
                            selectedTanggal,
                            pegawai.nama_pegawai
                          )
                        }
                      />
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {disposisiBeri[0]?.perihal ||
                          disposisiBeri[0]?.nomor_surat ||
                          "-"}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              );
            })()}
          </MDBox>
        ),
        pulang_apel_siang: (
          <MDBox>
            {(() => {
              const daftarHadirData = dataDaftarHadir[pegawai.uid_RSS];
              return (
                <>
                  {daftarHadirData?.waktuPulang && (
                    <MDBox mb={0.5}>
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        Pulang: {daftarHadirData.waktuPulang}
                      </MDTypography>
                      {daftarHadirData.workingHours && (
                        <Chip
                          label={`Jam Kerja: ${daftarHadirData.workingHours}`}
                          color="info"
                          size="small"
                          variant="filled"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </MDBox>
                  )}
                  {pegawai.apel_siang ? (
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      mt={0.5}
                    >
                      Apel: {pegawai.apel_siang.jam_absen || "-"}
                    </MDTypography>
                  ) : (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mt={0.5}
                    >
                      Belum Apel Siang
                    </MDTypography>
                  )}
                </>
              );
            })()}
          </MDBox>
        ),
        events: (
          <MDBox>
            {eventsArray.length > 0 ? (
              <MDBox>
                <Chip
                  label={`${eventsArray.length} Event`}
                  color="primary"
                  size="small"
                  sx={{ mb: 0.5, cursor: "pointer" }}
                  onClick={() =>
                    handleOpenDetailModal(
                      "events",
                      eventsArray,
                      selectedTanggal,
                      pegawai.nama_pegawai
                    )
                  }
                />
                <MDTypography variant="caption" color="text" display="block">
                  {eventsArray[0]?.nama_event || "-"}
                </MDTypography>
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            )}
          </MDBox>
        ),
        tamu: (
          <MDBox>
            {tamuArray.length > 0 ? (
              <MDBox>
                <Chip
                  label={`${tamuArray.length} Tamu`}
                  color="secondary"
                  size="small"
                  sx={{ mb: 0.5, cursor: "pointer" }}
                  onClick={() =>
                    handleOpenDetailModal(
                      "tamu",
                      tamuArray,
                      selectedTanggal,
                      pegawai.nama_pegawai
                    )
                  }
                />
                <MDTypography variant="caption" color="text" display="block">
                  {tamuArray[0]?.nama_tamu || "-"}
                </MDTypography>
              </MDBox>
            ) : (
              <MDTypography variant="caption" color="text.secondary">
                -
              </MDTypography>
            )}
          </MDBox>
        ),
      };
    }) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDTypography variant="h6" gutterBottom>
                    Statistik Laporan Harian
                  </MDTypography>
                  <MDBox>
                    <MDInput
                      type="date"
                      label="Pilih Tanggal"
                      value={selectedTanggal}
                      onChange={handleTanggalChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </MDBox>
                </MDBox>

                {dataLaporanDinas &&
                  (() => {
                    // Hitung statistik baru
                    const totalPegawai =
                      dataLaporanDinas.statistik.total_pegawai || 0;
                    const dataPegawai = dataLaporanDinas.data_pegawai || [];

                    // Hitung pegawai dengan aktivitas = 0
                    let tanpaAktivitas = 0;
                    let denganAktivitas = 0;

                    dataPegawai.forEach((pegawai) => {
                      const disposisiArray = Array.isArray(pegawai.disposisi)
                        ? pegawai.disposisi
                        : [];
                      const instruksiArray = Array.isArray(pegawai.instruksi)
                        ? pegawai.instruksi
                        : [];
                      const tamuArray = Array.isArray(pegawai.tamu)
                        ? pegawai.tamu
                        : [];
                      const eventsArray = Array.isArray(pegawai.events)
                        ? pegawai.events
                        : [];
                      const laporanArray = Array.isArray(pegawai.laporan_harian)
                        ? pegawai.laporan_harian
                        : [];

                      const totalActivities =
                        disposisiArray.length +
                        instruksiArray.length +
                        tamuArray.length +
                        eventsArray.length +
                        laporanArray.length;

                      if (totalActivities === 0) {
                        tanpaAktivitas++;
                      } else {
                        denganAktivitas++;
                      }
                    });

                    // Hitung persentase kepatuhan: (pegawai dengan aktivitas > 0) / total pegawai * 100
                    const persentaseKepatuhan =
                      totalPegawai > 0
                        ? Math.round((denganAktivitas / totalPegawai) * 100)
                        : 0;

                    return (
                      <>
                        <MDTypography
                          variant="button"
                          color="text"
                          gutterBottom
                        >
                          {dataLaporanDinas.nama_dinas} -{" "}
                          {new Date(
                            dataLaporanDinas.tanggal
                          ).toLocaleDateString("id-ID")}
                        </MDTypography>

                        {/* Statistik dengan Chips/Badges */}
                        <MDBox
                          mt={2}
                          display="flex"
                          flexWrap="wrap"
                          gap={1.5}
                          alignItems="center"
                        >
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>people</Icon>}
                            label={`Total Pegawai: ${totalPegawai}`}
                            color="info"
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              height: "32px",
                            }}
                          />
                          <Chip
                            icon={
                              <Icon sx={{ fontSize: 18 }}>inactive_order</Icon>
                            }
                            label={`Tanpa Aktivitas: ${tanpaAktivitas}`}
                            color="error"
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              height: "32px",
                            }}
                          />
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>assignment</Icon>}
                            label={`Total Aktivitas: ${
                              dataLaporanDinas.statistik.total_aktivitas || 0
                            }`}
                            color="primary"
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "medium",
                              height: "32px",
                            }}
                          />
                          <Chip
                            icon={
                              <Icon sx={{ fontSize: 18 }}>description</Icon>
                            }
                            label={`Laporan Harian: ${
                              dataLaporanDinas.statistik.total_laporan_harian ||
                              0
                            }`}
                            color="info"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>task</Icon>}
                            label={`Instruksi: ${
                              dataLaporanDinas.statistik.total_instruksi || 0
                            }`}
                            color="success"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>forward</Icon>}
                            label={`Disposisi: ${
                              dataLaporanDinas.statistik.total_disposisi || 0
                            }`}
                            color="warning"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>event</Icon>}
                            label={`Event: ${
                              dataLaporanDinas.statistik.total_events || 0
                            }`}
                            color="primary"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                          <Chip
                            icon={<Icon sx={{ fontSize: 18 }}>person_add</Icon>}
                            label={`Tamu: ${
                              dataLaporanDinas.statistik.total_tamu || 0
                            }`}
                            color="secondary"
                            size="small"
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: "medium",
                              height: "28px",
                            }}
                          />
                        </MDBox>

                        {/* Progress Bar */}
                        <MDBox mt={3}>
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <MDTypography variant="button" color="text">
                              Persentase Kepatuhan
                            </MDTypography>
                            <MDTypography
                              variant="button"
                              color={
                                persentaseKepatuhan >= 80
                                  ? "success"
                                  : persentaseKepatuhan >= 60
                                  ? "warning"
                                  : "error"
                              }
                            >
                              {persentaseKepatuhan}%
                            </MDTypography>
                          </MDBox>
                          <MDProgress
                            variant="determinate"
                            value={persentaseKepatuhan}
                            color={
                              persentaseKepatuhan >= 80
                                ? "success"
                                : persentaseKepatuhan >= 60
                                ? "warning"
                                : "error"
                            }
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </MDBox>
                      </>
                    );
                  })()}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataLaporanDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(selectedTanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataLaporanDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>

                  {/* Radio Button untuk Sorting */}
                  <MDBox mb={2}>
                    <MDBox mb={1}>
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="medium"
                      >
                        Urutkan Berdasarkan:
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" flexWrap="wrap" gap={2}>
                      <MDBox display="flex" alignItems="center">
                        <input
                          type="radio"
                          id="sort-bidang"
                          name="sortBy"
                          value="bidang"
                          checked={sortBy === "bidang"}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ marginRight: 8 }}
                        />
                        <label
                          htmlFor="sort-bidang"
                          style={{ cursor: "pointer" }}
                        >
                          <MDTypography variant="body2" color="text">
                            Bidang
                          </MDTypography>
                        </label>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <input
                          type="radio"
                          id="sort-aktivitas-desc"
                          name="sortBy"
                          value="aktivitas_desc"
                          checked={sortBy === "aktivitas_desc"}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ marginRight: 8 }}
                        />
                        <label
                          htmlFor="sort-aktivitas-desc"
                          style={{ cursor: "pointer" }}
                        >
                          <MDTypography variant="body2" color="text">
                            Aktivitas (Banyak → Sedikit)
                          </MDTypography>
                        </label>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <input
                          type="radio"
                          id="sort-aktivitas-asc"
                          name="sortBy"
                          value="aktivitas_asc"
                          checked={sortBy === "aktivitas_asc"}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ marginRight: 8 }}
                        />
                        <label
                          htmlFor="sort-aktivitas-asc"
                          style={{ cursor: "pointer" }}
                        >
                          <MDTypography variant="body2" color="text">
                            Aktivitas (Sedikit → Banyak)
                          </MDTypography>
                        </label>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <input
                          type="radio"
                          id="sort-level-jabatan"
                          name="sortBy"
                          value="level_jabatan"
                          checked={sortBy === "level_jabatan"}
                          onChange={(e) => setSortBy(e.target.value)}
                          style={{ marginRight: 8 }}
                        />
                        <label
                          htmlFor="sort-level-jabatan"
                          style={{ cursor: "pointer" }}
                        >
                          <MDTypography variant="body2" color="text">
                            Level Jabatan
                          </MDTypography>
                        </label>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {sortedDataPegawai.map((pegawai, index) => (
                        <Card
                          key={`${
                            pegawai.uid_RSS || pegawai.nama_pegawai || "row"
                          }-${index}`}
                        >
                          <CardContent>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={4}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <img
                                    src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
                                    alt={pegawai.nama_pegawai}
                                    style={{
                                      width: 72,
                                      height: 72,
                                      borderRadius: "50%",
                                      objectFit: "cover",
                                      border: "2px solid #e0e0e0",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                      e.target.nextSibling.style.display =
                                        "flex";
                                    }}
                                  />
                                  <MDBox
                                    display="none"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      width: 72,
                                      height: 72,
                                      borderRadius: "50%",
                                      backgroundColor: "grey.200",
                                      color: "grey.500",
                                    }}
                                  >
                                    <Icon sx={{ fontSize: 36 }}>person</Icon>
                                  </MDBox>
                                </MDBox>
                              </Grid>
                              <Grid item xs={8}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                >
                                  {index + 1}. {pegawai.nama_pegawai}
                                </MDTypography>
                                <MDBox mt={0.5}>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {pegawai.nama_jabatan}
                                  </MDTypography>
                                </MDBox>
                                <MDBox>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Bidang: {pegawai.nama_bidang || "-"} •
                                    Pangkat: {pegawai.pangkat || "-"}
                                  </MDTypography>
                                </MDBox>
                                <MDBox
                                  mt={1}
                                  display="flex"
                                  flexWrap="wrap"
                                  gap={0.5}
                                >
                                  {(() => {
                                    // Hitung total aktivitas: Disposisi + Instruksi + Tamu + Event + Laporan
                                    // Pastikan data adalah array, jika undefined/null gunakan array kosong
                                    const disposisiArray = Array.isArray(
                                      pegawai.disposisi
                                    )
                                      ? pegawai.disposisi
                                      : [];
                                    const instruksiArray = Array.isArray(
                                      pegawai.instruksi
                                    )
                                      ? pegawai.instruksi
                                      : [];
                                    const tamuArray = Array.isArray(
                                      pegawai.tamu
                                    )
                                      ? pegawai.tamu
                                      : [];
                                    const eventsArray = Array.isArray(
                                      pegawai.events
                                    )
                                      ? pegawai.events
                                      : [];
                                    const laporanArray = Array.isArray(
                                      pegawai.laporan_harian
                                    )
                                      ? pegawai.laporan_harian
                                      : [];

                                    const totalDisposisi =
                                      disposisiArray.length;
                                    const totalInstruksi =
                                      instruksiArray.length;
                                    const totalTamu = tamuArray.length;
                                    const totalEvents = eventsArray.length;
                                    const totalLaporan = laporanArray.length;

                                    const totalActivities =
                                      totalDisposisi +
                                      totalInstruksi +
                                      totalTamu +
                                      totalEvents +
                                      totalLaporan;

                                    if (totalActivities === 0) {
                                      return (
                                        <Chip
                                          label="No Activity"
                                          color="error"
                                          size="small"
                                        />
                                      );
                                    }

                                    return (
                                      <Chip
                                        label={`${totalActivities} Aktivitas`}
                                        color="info"
                                        size="small"
                                      />
                                    );
                                  })()}
                                </MDBox>

                                {/* Laporan Harian */}
                                {pegawai.laporan_harian &&
                                  pegawai.laporan_harian.length > 0 && (
                                    <MDBox
                                      mt={1}
                                      p={1}
                                      sx={{
                                        backgroundColor: "#e3f2fd",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="info"
                                      >
                                        Laporan Harian
                                      </MDTypography>
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${pegawai.laporan_harian.length} Laporan`}
                                          color="info"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "laporan_harian",
                                              pegawai.laporan_harian,
                                              selectedTanggal,
                                              pegawai.nama_pegawai
                                            )
                                          }
                                        />
                                      </MDBox>
                                    </MDBox>
                                  )}

                                {/* Instruksi */}
                                {pegawai.instruksi &&
                                  pegawai.instruksi.length > 0 && (
                                    <MDBox
                                      mt={1}
                                      p={1}
                                      sx={{
                                        backgroundColor: "#e8f5e9",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="success"
                                      >
                                        Instruksi Selesai
                                      </MDTypography>
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${pegawai.instruksi.length} Instruksi`}
                                          color="success"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "instruksi",
                                              pegawai.instruksi,
                                              selectedTanggal,
                                              pegawai.nama_pegawai
                                            )
                                          }
                                        />
                                      </MDBox>
                                    </MDBox>
                                  )}

                                {/* Disposisi */}
                                {pegawai.disposisi &&
                                  pegawai.disposisi.length > 0 && (
                                    <MDBox
                                      mt={1}
                                      p={1}
                                      sx={{
                                        backgroundColor: "#fff3e0",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="warning"
                                      >
                                        Disposisi Selesai
                                      </MDTypography>
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${pegawai.disposisi.length} Disposisi`}
                                          color="warning"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "disposisi",
                                              pegawai.disposisi,
                                              selectedTanggal,
                                              pegawai.nama_pegawai
                                            )
                                          }
                                        />
                                      </MDBox>
                                    </MDBox>
                                  )}

                                {/* Events */}
                                {pegawai.events &&
                                  pegawai.events.length > 0 && (
                                    <MDBox
                                      mt={1}
                                      p={1}
                                      sx={{
                                        backgroundColor: "#f3e5f5",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        color="primary"
                                      >
                                        Event
                                      </MDTypography>
                                      <MDBox mt={0.5}>
                                        <Chip
                                          label={`${pegawai.events.length} Event`}
                                          color="primary"
                                          size="small"
                                          sx={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleOpenDetailModal(
                                              "events",
                                              pegawai.events,
                                              selectedTanggal,
                                              pegawai.nama_pegawai
                                            )
                                          }
                                        />
                                      </MDBox>
                                    </MDBox>
                                  )}

                                {/* Tamu */}
                                {pegawai.tamu && pegawai.tamu.length > 0 && (
                                  <MDBox
                                    mt={1}
                                    p={1}
                                    sx={{
                                      backgroundColor: "#fce4ec",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="secondary"
                                    >
                                      Tamu
                                    </MDTypography>
                                    <MDBox mt={0.5}>
                                      <Chip
                                        label={`${pegawai.tamu.length} Tamu`}
                                        color="secondary"
                                        size="small"
                                        sx={{ cursor: "pointer" }}
                                        onClick={() =>
                                          handleOpenDetailModal(
                                            "tamu",
                                            pegawai.tamu,
                                            selectedTanggal,
                                            pegawai.nama_pegawai
                                          )
                                        }
                                      />
                                    </MDBox>
                                  </MDBox>
                                )}
                                {userStatus === "admin" && (
                                  <MDBox mt={1}>
                                    <MDButton
                                      variant="outlined"
                                      color="info"
                                      size="small"
                                      onClick={() => handleOpenDialog(pegawai)}
                                      sx={{ minWidth: "auto", px: 1 }}
                                    >
                                      <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                      &nbsp;Lapor
                                    </MDButton>
                                  </MDBox>
                                )}
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State - hanya tampil jika belum ada data sama sekali */}
          {loading && !dataLaporanDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  p={3}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                >
                  <CircularProgress size={48} />
                  <MDTypography variant="body1" color="text" mt={2}>
                    Memuat data laporan dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataLaporanDinas && !loading && userDinasId && selectedTanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data laporan harian
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal lain atau periksa kembali dinas
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Dialog Form Laporan */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                Tambah Laporan Harian - {selectedPegawai?.nama_pegawai}
              </MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tanggal"
                    type="date"
                    value={form.tanggal}
                    onChange={(e) =>
                      setForm({ ...form, tanggal: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nama Pegawai"
                    value={selectedPegawai?.nama_pegawai || ""}
                    variant="outlined"
                    disabled
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDTypography variant="h6">Daftar Kegiatan</MDTypography>
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={handleAddKegiatan}
                      startIcon={<AddIcon />}
                    >
                      Tambah Kegiatan
                    </MDButton>
                  </MDBox>

                  {form.kegiatan.map((kegiatan, index) => (
                    <Card key={index} sx={{ mb: 2, p: 2 }}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <MDTypography variant="h6">
                          Kegiatan {index + 1}
                        </MDTypography>
                        {form.kegiatan.length > 1 && (
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveKegiatan(index)}
                          >
                            <RemoveIcon />
                          </IconButton>
                        )}
                      </MDBox>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Kegiatan"
                            value={kegiatan.kegiatan}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "kegiatan",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Output"
                            value={kegiatan.output}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "output",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            multiline
                            rows={2}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Jumlah Output"
                            type="number"
                            value={kegiatan.jumlah_output}
                            onChange={(e) =>
                              handleKegiatanChange(
                                index,
                                "jumlah_output",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            inputProps={{ min: 1 }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Autocomplete
                            options={satuanOutputOptions}
                            value={kegiatan.satuan_output}
                            onChange={(event, newValue) =>
                              handleKegiatanChange(
                                index,
                                "satuan_output",
                                newValue || ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Satuan Output"
                                variant="outlined"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDialog}>
              Batal
            </MDButton>
            <MDButton color="info" variant="contained" onClick={handleSubmit}>
              Simpan
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Kegiatan */}
        <Dialog
          open={openKegiatanModal}
          onClose={handleCloseKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{selectedLaporanTitle}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {loadingKegiatan ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Memuat data kegiatan...
                  </MDTypography>
                </MDBox>
              ) : !Array.isArray(selectedKegiatan) ||
                selectedKegiatan.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada kegiatan untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox mb={3}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Total Kegiatan: {selectedKegiatan.length}
                    </MDTypography>
                  </MDBox>
                  <Grid container spacing={2}>
                    {selectedKegiatan.map((kegiatan, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, mb: 2 }}>
                          <MDBox>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={2}
                            >
                              <MDTypography
                                variant="h6"
                                color="info"
                                gutterBottom
                              >
                                Kegiatan {index + 1}
                              </MDTypography>
                              {userStatus === "admin" && (
                                <MDBox display="flex" gap={1}>
                                  <IconButton
                                    color="info"
                                    size="small"
                                    onClick={() => handleEditKegiatan(kegiatan)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                      handleDeleteKegiatan(kegiatan)
                                    }
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </MDBox>
                              )}
                            </MDBox>

                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Deskripsi Kegiatan:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.kegiatan}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={12}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Jumlah Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.jumlah_output}
                                </MDTypography>
                              </Grid>

                              <Grid item xs={6}>
                                <MDTypography
                                  variant="subtitle2"
                                  color="text"
                                  gutterBottom
                                >
                                  Satuan Output:
                                </MDTypography>
                                <MDTypography variant="body2" color="text">
                                  {kegiatan.satuan_output}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseKegiatanModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Edit Kegiatan */}
        <Dialog
          open={openEditKegiatanModal}
          onClose={handleCloseEditKegiatanModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">Edit Kegiatan</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Kegiatan"
                    value={editKegiatanForm.kegiatan}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        kegiatan: e.target.value,
                      })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Output"
                    value={editKegiatanForm.output}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        output: e.target.value,
                      })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Jumlah Output"
                    type="number"
                    value={editKegiatanForm.jumlah_output}
                    onChange={(e) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        jumlah_output: e.target.value,
                      })
                    }
                    variant="outlined"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={satuanOutputOptions}
                    value={editKegiatanForm.satuan_output}
                    onChange={(event, newValue) =>
                      setEditKegiatanForm({
                        ...editKegiatanForm,
                        satuan_output: newValue || "",
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Satuan Output"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseEditKegiatanModal}>
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleUpdateKegiatan}
            >
              Update
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Detail Modal */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">{detailModalData.title}</MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {detailModalData.data.length === 0 ? (
                <MDBox textAlign="center" py={4}>
                  <MDTypography variant="body1" color="text">
                    Tidak ada data untuk ditampilkan
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  {detailModalData.type === "laporan_harian" && (
                    <MDBox>
                      {detailModalData.data.map((laporan, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="info"
                              gutterBottom
                            >
                              Laporan #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Tanggal:{" "}
                              {new Date(laporan.tanggal).toLocaleDateString(
                                "id-ID"
                              )}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Jumlah Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.jumlah_kegiatan || 0}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                ID Laporan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {laporan.id_laporan}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "instruksi" && (
                    <MDBox>
                      {detailModalData.data.map((instruksi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="success"
                              gutterBottom
                            >
                              Instruksi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {instruksi.waktu_selesai
                                ? new Date(
                                    instruksi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Isi Instruksi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.isi_instruksi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Prioritas:
                              </MDTypography>
                              <Chip
                                label={instruksi.prioritas || "-"}
                                color={
                                  instruksi.prioritas === "Tinggi"
                                    ? "error"
                                    : instruksi.prioritas === "Sedang"
                                    ? "warning"
                                    : "default"
                                }
                                size="small"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {instruksi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "disposisi" && (
                    <MDBox>
                      {detailModalData.data.map((disposisi, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="warning"
                              gutterBottom
                            >
                              Disposisi #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Selesai:{" "}
                              {disposisi.waktu_selesai
                                ? new Date(
                                    disposisi.waktu_selesai
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Perihal:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.perihal || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nomor Surat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.nomor_surat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Pemberi Perintah:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {disposisi.pemberi_perintah || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "events" && (
                    <MDBox>
                      {detailModalData.data.map((event, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="primary"
                              gutterBottom
                            >
                              Event #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Absen:{" "}
                              {event.waktu
                                ? new Date(event.waktu).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Event:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.nama_event || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Detail Kegiatan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.detail_kegiatan || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Tempat:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.tempat || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Mulai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_mulai
                                  ? new Date(event.waktu_mulai).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Waktu Selesai:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {event.waktu_finish
                                  ? new Date(event.waktu_finish).toLocaleString(
                                      "id-ID"
                                    )
                                  : "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}

                  {detailModalData.type === "tamu" && (
                    <MDBox>
                      {detailModalData.data.map((tamu, index) => (
                        <Card key={index} sx={{ mb: 2, p: 2 }}>
                          <MDBox mb={2}>
                            <MDTypography
                              variant="h6"
                              color="secondary"
                              gutterBottom
                            >
                              Tamu #{index + 1}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              Waktu Kedatangan:{" "}
                              {tamu.waktu_kedatangan
                                ? new Date(
                                    tamu.waktu_kedatangan
                                  ).toLocaleString("id-ID")
                                : "-"}
                            </MDTypography>
                          </MDBox>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Nama Tamu:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_tamu || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Instansi:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.nama_instansi || "-"}
                              </MDTypography>
                            </Grid>
                            <Grid item xs={12}>
                              <MDTypography
                                variant="subtitle2"
                                color="text"
                                gutterBottom
                              >
                                Keperluan:
                              </MDTypography>
                              <MDTypography variant="body2" color="text">
                                {tamu.keperluan || "-"}
                              </MDTypography>
                            </Grid>
                          </Grid>
                        </Card>
                      ))}
                    </MDBox>
                  )}
                </>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Loading */}
        <Dialog
          open={loading}
          disableEscapeKeyDown
          disableBackdropClick
          maxWidth="xs"
          fullWidth
        >
          <DialogContent>
            <MDBox
              p={3}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={48} />
              <MDTypography variant="body1" color="text" mt={2}>
                Memuat data laporan dinas...
              </MDTypography>
            </MDBox>
          </DialogContent>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default LaporanDinas;
